import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { IncludedItems } from "../components/checkout/included-items";
import { useTranslation } from "react-i18next";
import { OrderResponse } from "@/store/types/order";
import { parseMetadataNumber } from "@/lib/utils/numbers";
import {
  getPromotionMonthCount,
  isEndlessPromotion,
  parsePromotion,
} from "@/lib/promotion";
import checkmark from "@/assets/check-mark-accent.svg";

export default function OrderSummary({ order }: { order: OrderResponse }) {
  const { t } = useTranslation();
  const monthlyPrice = parseMetadataNumber(
    order.metadata?.["Flex Monthly Price"],
    0
  );

  const bindingPeriod = parseMetadataNumber(
    order.metadata?.["Flex Binding Period"],
    12
  );

  const promotion = parsePromotion(order.metadata?.promotion);
  const hasPromotion = !!promotion;

  // Validate required metadata
  if (monthlyPrice === 0) {
    throw new Error("Monthly price is required in order metadata");
  }

  // Filter for actual product SKUs (starting with 'R')
  const productItems =
    order.line_items?.filter((item) => item.sku_code?.startsWith("R")) || [];

  if (productItems.length === 0) {
    throw new Error("No valid product items found in order");
  }

  const showAfterPromotionPrice =
    hasPromotion && promotion.appliedPrice < monthlyPrice;

  const afterPromotionPriceRow = showAfterPromotionPrice ? (
    <div className="flex justify-between items-center">
      <span className="text-sm font-normal">{t("orderSummary.thereafter")}</span>
      <span className="text-sm font-semibold">
        {t("orderSummary.pricePerMonth", { price: monthlyPrice })}
      </span>
    </div>
  ) : null;

  const renderPrice = () => {
    if (!hasPromotion) {
      return (
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold">{t("orderSummary.total")}</span>
          <span className="text-sm font-bold">
            {monthlyPrice}{" "}
            {t("orderSummary.currencyPerMonth", { currency: "kr" })}
          </span>
        </div>
      );
    }

    const validityPeriod = promotion.validityPeriod;
    const promotionMonths = getPromotionMonthCount(
      validityPeriod,
      bindingPeriod
    );

    if (isEndlessPromotion(validityPeriod)) {
      return (
        <div className="space-y-1">
          <p className="text-sm font-semibold">{t("orderSummary.youPay")}</p>
          <div className="flex justify-between items-center">
            <span className="text-sm font-normal flex items-center">
              {t("orderSummary.months", { count: bindingPeriod })}
              <img
                className="ml-1 h-4 w-4"
                src={checkmark}
                alt="Promotion Checkmark"
              />
            </span>
            <span className="text-sm font-semibold">
              {t("orderSummary.pricePerMonth", {
                price: promotion.appliedPrice,
              })}
            </span>
          </div>
          {afterPromotionPriceRow}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <p className="text-sm font-semibold">{t("orderSummary.youPay")}</p>
        <div className="flex justify-between items-center">
          <span className="text-sm font-normal flex items-center">
            {t("orderSummary.monthRange", {
              range: promotionMonths === 1 ? "1" : `1-${promotionMonths}`,
            })}{" "}
            <img
              className="ml-1 h-4 w-4"
              src={checkmark}
              alt="Promotion Checkmark"
            />
          </span>
          <span className="text-sm font-semibold">
            {t("orderSummary.pricePerMonth", {
              price: promotion.appliedPrice,
            })}
          </span>
        </div>
        {afterPromotionPriceRow}
      </div>
    );
  };

  const calculateTotalCost = () => {
    if (!hasPromotion) return monthlyPrice * bindingPeriod;

    const validityPeriod = promotion.validityPeriod;
    if (isEndlessPromotion(validityPeriod)) {
      return promotion.appliedPrice * bindingPeriod;
    }

    const promotionMonths = getPromotionMonthCount(
      validityPeriod,
      bindingPeriod
    );
    return (
      promotion.appliedPrice * promotionMonths +
      monthlyPrice * (bindingPeriod - promotionMonths)
    );
  };

  return (
    <Card className="w-full mb-6">
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Product Items */}
          {productItems.map((item) => (
            <div key={item.id} className="flex justify-between items-start">
              <div className="space-y-1 flex items-start gap-3">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.name || "Product Image"}
                    className="w-12 h-12 object-contain rounded-md"
                  />
                )}
                <div>
                  <div className="font-bold">{item.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {hasPromotion ? (
                      <>
                        <span className="line-through">{monthlyPrice} kr</span>{" "}
                        <span className="text-asparagus-700 font-medium no-underline">
                          {t("orderSummary.pricePerMonth", {
                            price: promotion.appliedPrice,
                          })}
                        </span>
                      </>
                    ) : (
                      <>
                        {monthlyPrice}{" "}
                        {t("orderSummary.currencyPerMonth", { currency: "kr" })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Separator />

          <IncludedItems />

          <Separator />

          {/* Extra Info */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">
                {t("orderSummary.shipping")}
              </span>
              <span className="text-sm font-semibold">
                {t("orderSummary.freeShipping")}
              </span>
            </div>

            {renderPrice()}

            {/*             

            <div className="flex justify-between items-center">
              <span className="text-sm font-bold">
                {t("orderSummary.total")}
              </span>
              <span className="text-sm font-bold">
                {monthlyPrice}{" "}
                {t("orderSummary.currencyPerMonth", { currency: "kr" })}
              </span>
            </div> */}
            <Separator />

            <p className="text-xs text-center text-muted-foreground pt-2">
              {t("orderSummary.totalCostNotice", {
                bindingPeriod,
                totalPrice: calculateTotalCost(),
                currency: "kr",
              })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
